import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Box, Button, Tab, Tabs } from "@mui/material";
import { toast } from "react-toastify";
import { isCreateAllowed, isStoreRole } from "../../utils/common-utils";
import PRList from "./pr-list";
import PRCreateModal from "./create-pr/pr-modal";
import PRApprovalList from "./pr-approval-list";
import { HTTP_METHODS, invokeApi } from "../../utils/http-service";
import { FINANCE_V2_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../common-utilities/utils/end-points/finance-v2";
import "./index.scss";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pr-tabpanel-${index}`}
      aria-labelledby={`pr-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const PurchaseRequisition = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTabIndex = searchParams.get("index");
  const openSubIndex = searchParams.get("subIndex");
  const [value, setValue] = useState(+openSubIndex || 0);
  const [prModal, setPRModal] = useState(false);
  const [noOfPendingPr, setNoOfPendingPr] = useState(0);

  // STORE role: no scope-selection launcher -- resolve the caller's own store + its single
  // active OPEX budget server-side, then go straight into PR creation with that context
  // locked. Blocks with the backend's config error (unresolved store / zero or multiple
  // active OPEX budgets) instead of navigating.
  const handleCreatePRClick = () => {
    if (!isStoreRole()) {
      setPRModal(true);
      return;
    }
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.STORE_PR_CONTEXT}`,
      null,
      {}
    )
      .then((response) => {
        if (response?.message) {
          toast.error(response.message);
          return;
        }
        const { site, budget, financialYear } = response;
        navigate(
          `purchase-requisition/create/${budget._id}` +
            `?prType=OPEX` +
            `&prScope=SINGLE_STORE` +
            `&financialYear=${financialYear}` +
            `&siteId=${site._id}`
        );
      })
      .catch(() => {
        toast.error("Unable to start a Purchase Requisition for your store. Please try again.");
      });
  };

  const handleChange = (event, newValue) => {
    setSearchParams({ index: mainTabIndex || 0, subIndex: newValue });
    setValue(newValue);
  };

  const getPendingPr = () => {
    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_MY_APPROVALS}`,
      null,
      { approvalType: "pr_approval", status: "approval_in_progress" }
    )
      .then((response) => {
        if (!response.message) {
          setNoOfPendingPr(response?.length || 0);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getPendingPr();
  }, []);

  return (
    <div className="finance-v2-rm-pt finance-v2-budget-list">
      <div className="tabs-section">
        <Box>
          <Tabs
            variant="scrollable"
            value={value}
            onChange={handleChange}
            sx={{
              borderBottom: "1px solid #DDE2EC",
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                color: "#5B6E8A",
                minWidth: "unset",
                borderRadius: "20px",
                margin: "0 2px",
                textTransform: "none",
                "& .MuiBadge-root": {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                },
                "& .MuiBadge-badge": {
                  position: "relative",
                  transform: "none",
                  top: "auto",
                  right: "auto",
                  background: "transparent",
                  color: "#007A42",
                  minWidth: "unset",
                  height: "auto",
                  padding: 0,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  lineHeight: 1,
                  borderRadius: 0,
                  boxShadow: "none",
                  "&.MuiBadge-invisible": { display: "none" },
                },
              },
              "& .Mui-selected": {
                color: "#005F35 !important",
                background: "#EAF4EE",
                "& .MuiBadge-badge": {
                  background: "#005F35",
                  color: "#fff",
                  minWidth: "16px",
                  height: "16px",
                  padding: "0 3px",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  borderRadius: "50%",
                },
              },
            }}
            TabIndicatorProps={{ sx: { display: "none" } }}
          >
            <Tab label="Draft" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="Approval in Progress" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="Approved" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="Rejected" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="Reconsider" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="All PRs" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab label="Converted to PO" sx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }} />
            <Tab
              label={
                <Badge badgeContent={noOfPendingPr} color="primary">
                  PENDING ACTIONS
                </Badge>
              }
              ssx={{ paddingX: 3, fontSize: "0.875rem !important", textTransform: "uppercase !important" }}
            />
          </Tabs>
        </Box>
        {isCreateAllowed("pr") && (
          <Button variant="outlined" onClick={handleCreatePRClick}>
            Create PR
          </Button>
        )}
      </div>

      <TabPanel value={value} index={0}>
        {/* "Draft" groups a brand-new PR (status "created") together with one that was
            live and got reverted by an edit (status "Draft") -- both are still a draft
            from the user's point of view. See getPurchaseRequests' comma-separated
            status -> $in handling on the backend. */}
        <PRList status="created,Draft" />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <PRList status="approval_in_progress" />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <PRList status="approved" />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <PRList status="rejected" />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <PRList status="reconsider" />
      </TabPanel>
      <TabPanel value={value} index={5}>
        <PRList status="all" />
      </TabPanel>
      <TabPanel value={value} index={6}>
        <PRList status="converted" />
      </TabPanel>
      <TabPanel value={value} index={7}>
        <PRApprovalList
          searchPlaceHolder="Search by PR Number"
          approvalType="pr_approval"
          status="approval_in_progress"
        />
      </TabPanel>

      {prModal && <PRCreateModal onClose={() => setPRModal(false)} />}
    </div>
  );
};

export default PurchaseRequisition;
